import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@heartinternet/hi-ui';

interface CogRow {
  packageOid: string;
  kind: 'notification' | 'package';
}

export function useNotificationsCog() {
  const [openOid, setOpenOid] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [menuLabel, setMenuLabel] = useState<string>('Edit');
  const [menuHref, setMenuHref] = useState<string>('#');
  const triggerElRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openDropdown = useCallback((row: CogRow, buttonEl: HTMLButtonElement) => {
    triggerElRef.current = buttonEl;
    const rect = buttonEl.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 4, left: rect.right });
    setMenuLabel(row.kind === 'notification' ? 'Edit' : 'Add notification');
    setMenuHref(`/manage/manage-package-details.cgi?id=${row.packageOid}`);
    setOpenOid(row.packageOid);
  }, []);

  const closeDropdown = useCallback(() => {
    setOpenOid(null);
    setMenuPosition(null);
  }, []);

  /* v8 ignore start -- JSDOM getBoundingClientRect returns zeros, untestable without a real browser */
  useLayoutEffect(() => {
    if (!menuPosition || !menuRef.current) return;
    const menuRect = menuRef.current.getBoundingClientRect();
    if (menuRect.bottom > window.innerHeight || menuRect.left < 0) {
      const triggerRect = triggerElRef.current?.getBoundingClientRect();
      if (!triggerRect) return;
      if (menuRect.bottom > window.innerHeight) {
        menuRef.current.style.top = `${triggerRect.top - menuRect.height - 4}px`;
      }
      if (menuRect.left < 0) {
        menuRef.current.style.left = `${triggerRect.left}px`;
      }
    }
  });
  /* v8 ignore stop */

  useEffect(() => {
    if (!openOid) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        (triggerElRef.current && triggerElRef.current.contains(target)) ||
        (menuRef.current && menuRef.current.contains(target))
      ) {
        return;
      }
      closeDropdown();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openOid, closeDropdown]);

  const renderTrigger = useCallback(
    (row: CogRow) => {
      const isOpen = openOid === row.packageOid;
      return (
        <div
          className={`hi-ui-notifications-page__cog-wrapper${isOpen ? ' hi-ui-notifications-page__cog-wrapper--open' : ''}`}
          /* v8 ignore start -- hover handlers depend on mouse events not reliable in JSDOM */
          onMouseEnter={(e) => {
            if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
            const btn = (e.currentTarget as HTMLElement).querySelector(
              '.hi-ui-notifications-page__cog-trigger'
            ) as HTMLButtonElement | null;
            if (btn) openDropdown(row, btn);
          }}
          onMouseLeave={() => {
            if (isOpen) {
              hoverTimeoutRef.current = setTimeout(() => closeDropdown(), 150);
            }
          }}
          /* v8 ignore stop */
        >
          <button
            type="button"
            className="hi-ui-notifications-page__cog-trigger"
            onClick={(e) => {
              e.stopPropagation();
              if (isOpen) {
                closeDropdown();
              } else {
                openDropdown(row, e.currentTarget);
              }
            }}
            aria-label={`${row.kind === 'notification' ? 'Edit' : 'Add'} notification for ${row.packageOid}`}
          >
            <Icon name="cog" size={20} />
          </button>
        </div>
      );
    },
    [openOid, openDropdown, closeDropdown]
  );

  const renderPortal = useCallback(() => {
    if (!openOid || !menuPosition) return null;
    return createPortal(
      <div
        ref={menuRef}
        className="hi-ui-notifications-page__dropdown"
        style={{
          position: 'fixed',
          top: menuPosition.top,
          left: menuPosition.left,
          transform: 'translateX(-100%)',
        }}
        /* v8 ignore start -- portal hover handlers untestable in JSDOM */
        onMouseEnter={() => {
          if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
        }}
        onMouseLeave={() => {
          hoverTimeoutRef.current = setTimeout(() => closeDropdown(), 150);
        }}
        /* v8 ignore stop */
      >
        <a className="hi-ui-notifications-page__dropdown-item" href={menuHref}>
          {menuLabel}
        </a>
      </div>,
      document.body
    );
  }, [openOid, menuPosition, menuLabel, menuHref, closeDropdown]);

  return { renderTrigger, renderPortal };
}
